import { AppointmentLocationMode } from 'src/api/appointments';

export type RangeKey = '14_days' | '1_month' | '3_months' | '6_months' | '1_year';
export const RANGE_KEYS: RangeKey[] = ['14_days', '1_month', '3_months', '6_months', '1_year'];

export interface DeltaValue {
    direction: 'up' | 'down' | 'flat';
    amount: string;
}

export interface KpiTileData {
    id: 'contacts' | 'appointments' | 'customers' | 'orders';
    label: string;
    icon: string;
    value: number;
    delta: DeltaValue;
}

export interface LeadCondition {
    id: string;
    titleKey: string;
    valuePercent: number;
    valueLabelKey: string;
    valueLabelParams?: Record<string, number>;
}

export interface LeadAccess {
    id: 'free' | 'market';
    titleKey: string;
    icon: string; // @material-ui/icons glyph name resolved by the component
    unlocked: boolean;
}

export type TodoType = 'appointment' | 'lead' | 'followup' | 'offer' | 'match';

export interface TodoItemOffer {
    offerNr: string;
    phone: string;
    email: string;
    expiresOn: string;
}

export interface TodoItemData {
    id: string;
    type: TodoType;
    dueDate: string;
    dueTime?: string;
    name: string;
    company?: string;
    registered?: boolean;
    snippet: string;
    offer?: TodoItemOffer;
    appointmentTypeKey?: string;
    // Lead-assignment id for `lead` items; drives the accept/decline endpoints.
    leadAssignmentId?: string;
    /** When the lead offer was assigned (Europe/Vienna), for the Lead-annehmen dialog. */
    leadAssignedDate?: string;
    leadAssignedTime?: string;
    // Academy name-list entry id (lead + appointment/followup items); drives the history dialog and
    // rehydrates the full entry for the appointment/follow-up edit dialogs.
    nameListEntryId?: string;
    // Contact identity for customer-modal navigation (appointment/followup items); customerId is set
    // only for registered contacts.
    customerId?: number;
    firstName?: string;
    lastName?: string;
    candidateStatus?: 'Partner' | 'Customer';
    // Reminder id for reminder-backed followup items — routes "Aktion festlegen" to the reminder
    // resolve endpoint; legacy CONTROL follow-ups carry none and keep the funnel dialogs.
    reminderId?: string;
    /** Match rows: the detected (unlinked) registration shown in the confirm dialog. */
    matchedCustomerName?: string;
    matchedCustomerRegisteredOn?: string;
    // Follow-up priority (NORMAL | HIGH | LOW); drives the To-Do priority chip (todoDisplay.priorityChip).
    priority?: string;
    /** Live-Runde 10, appointment items: the appointment the row actions address, and its Ort. */
    appointmentId?: string;
    locationMode?: AppointmentLocationMode;
}

export interface RankingEntry {
    rank: number;
    /** The ranked value: revenue amount or held appointments. */
    value: number;
    isMe?: boolean;
    name?: string;
    /** 0..1 skeleton bar width of an anonymised row. */
    nameWidthHint?: number;
}

export interface RankingBoard {
    total: number;
    /** Null outside the ranking universe. */
    rank: number | null;
    of: number;
    entries: RankingEntry[];
}

export interface OfferLineItem {
    name: string;
    articleGroup?: string;
    quantity: number;
    unitNetPrice: number;
    lineNetTotal: number;
    vat: number;
}

/** Derived offer summary; the rows reconcile to `OfferRow.amount`: subtotal + discount + delivery + vat. */
export interface OfferSummary {
    subtotal: number; // Σ positive (goods) net of non-delivery lines
    discount: number; // Σ negative (rebate) net of non-delivery lines, ≤ 0
    delivery: number; // delivery-product gross (0 ⇒ "Kostenlos")
    vat: number; // amount − subtotal − discount − delivery
    vatRate?: number; // single VAT rate if all taxed lines share one, else undefined
}

export interface OfferAddress {
    addresseeName: string;
    addresseeName2?: string;
    contactName?: string; // "z. Hd." recipient, omitted when empty or identical to addresseeName
    street: string;
    postalCity: string;
    country?: string;
    vatNumber?: string;
}

export interface OfferRow {
    offerNr: string;
    date: string;
    customer: string;
    customerId?: string;
    // Discrete name parts (the DTO carries them; the `customer` display string joins them) — needed to
    // build the OrgPerson for the customer drill-down modals.
    firstName?: string;
    lastName?: string;
    registered: boolean;
    validUntil: string;
    status: 'new' | 'released' | 'open' | 'expiring' | 'accepted' | 'rejected' | 'expired';
    /** The stored offer state (new = draft, offered = released to the customer, ordered, cancelled, expired). */
    state?: string;
    amount: number;
    lineItems: OfferLineItem[];
    summary: OfferSummary;
    billingAddress?: OfferAddress;
    deliveryAddress?: OfferAddress;
    acceptedOn?: string; // ISO date the offer was accepted (orderedOn); absent until accepted
    paymentMethod?: string; // raw payment-method identifier (PREPAYMENT, LEASING, …)
    billedOn?: string; // ISO date the offer was billed (invoiceDate); absent until invoiced
    installedOn?: string; // ISO date the machine was set up ("Aufgestellt"); absent until the install workflow marks it
    installable?: boolean; // whether this offer's product is installable (machine); gates the "Aufgestellt" milestone. Defined in a later task — false for now
}

export interface OrderRow {
    orderNr: string;
    date: string;
    customer: string;
    company?: string;
    customerNr?: string;
    customerId?: number; // numeric customer id — drives the customer drill-down modals

    products?: string;
    /** R29-5: every article group of the order once, display names joined. */
    articleGroups?: string;
    paymentMethod?: string;
    status: 'accepted' | 'processing' | 'completed' | 'cancelled';
    paid: boolean;
    shippingState: 'open' | 'in_progress' | 'delivered';
    amount: number;
    amountNet?: number;
    amountGross?: number;
}

export type ListViewKey = 'todo' | 'contacts' | 'appointments' | 'orders' | 'offers';
export const LIST_VIEW_KEYS: ListViewKey[] = ['todo', 'contacts', 'appointments', 'orders', 'offers'];
/** `?view=offers` opens the dashboard list on that view (e.g. after an offer was saved in the Warenkorb). */
export const LIST_VIEW_QUERY_PARAM = 'view';

/** Grand-total badge counts for the list views (range/filter-independent). Todo is counted separately. */
export interface ListViewCounts {
    contacts: number;
    appointments: number;
    orders: number;
    offers: number;
}

/** first_meeting / first_follow_up / follow_up follow the contact's order (Live-Runde 10, Q955 A). */
export type AppointmentType = 'first_meeting' | 'first_follow_up' | 'follow_up' | 'phone' | 'wiedervorlage';

/** Pill state of a Termine row, derived server-side (Vienna day boundaries + recorded outcome). */
export type AppointmentRowState = 'planned' | 'today' | 'overdue' | 'held' | 'not_held';

export interface AppointmentRow {
    id: string;
    date: string; // ISO date
    time: string; // 'HH:MM'
    customer: string;
    company?: string;
    type: AppointmentType;
    // null for rows planned without an Ort
    location: 'on_site' | 'online' | null;
    note?: string;
    // Absent while an older academy build is live (deploy-gap); the list derives a fallback.
    state?: AppointmentRowState;
    // Contact identity (from academy via userservice) for the row-menu actions: nameListEntryId drives
    // the success-list dialogs, customerId the customer drill-down modals, registered the register item.
    nameListEntryId?: string;
    customerId?: number;
    firstName?: string;
    lastName?: string;
    registered?: boolean;
    /** Live-Runde 10: the Ort as the Termin planen pop-up stores it. */
    locationMode?: AppointmentLocationMode;
}
